import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import useThemeStyles from '../../../theme/useThemeStyles';

function Step1Account({ formData, referralCode, onComplete, onError }) {
  const styles = useThemeStyles();
  const _navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referrerName, setReferrerName] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [accountData, setAccountData] = useState({
    email: formData.email || '',
    password: '',
    confirmPassword: '',
    firstName: formData.firstName || '',
    lastName: formData.lastName || ''
  });

  // Fetch referral config when referral code is present
  useEffect(() => {
    const fetchReferralConfig = async () => {
      if (!referralCode) {
        setReferrerName(null);
        return;
      }

      try {
        setLoadingReferral(true);
        const response = await api.get(`/api/referrals/config/${referralCode}`);
        if (response.data && response.data.referrerName) {
          setReferrerName(response.data.referrerName);
        }
      } catch (error) {
        // Silently fail - referral code might not exist, that's okay
        setReferrerName(null);
      } finally {
        setLoadingReferral(false);
      }
    };

    fetchReferralConfig();
  }, [referralCode]);

  const handleChange = (field) => (event) => {
    setAccountData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (accountData.password !== accountData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate required fields
    if (!accountData.email || !accountData.password || !accountData.firstName || !accountData.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      const response = await api.post('/api/supplier/register', {
        email: accountData.email,
        password: accountData.password,
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        referralCode: referralCode || null
      });
      
      if (response.data.userId) {
        onComplete({
          ...accountData,
          userId: response.data.userId,
          emailSent: response.data.emailSent
        });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed';
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1, ...styles.text.primary }}>
        Create Your Account
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, ...styles.text.secondary }}>
        First things first – let's get you logged in.
      </Typography>

      {referralCode && (loadingReferral || referrerName) && (
        <Alert 
          icon={false}
          sx={{ 
            mb: 3,
            backgroundColor: '#fff3f8',
            border: '1px solid #ffb3d9',
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%',
              textAlign: 'center'
            }
          }}
        >
          {loadingReferral ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading...</Typography>
            </Box>
          ) : referrerName ? (
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              💕 The Amazing <strong style={{ color: '#e91e63' }}>{referrerName}</strong> has invited you to become part of the idotoo community! 💕
            </Typography>
          ) : null}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={accountData.firstName}
              onChange={handleChange('firstName')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={accountData.lastName}
              onChange={handleChange('lastName')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={accountData.email}
              onChange={handleChange('email')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={accountData.password}
              onChange={handleChange('password')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={accountData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

export default Step1Account;

